import React, { useState, useEffect } from 'react';
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../../baseurl';
import { Edit3, Layers, Sliders, Hash, Trash2, X, Check, Plus, ChevronLeft, ChevronRight } from 'lucide-react';

const Featurelayers = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'layer_order', direction: 'asc' });
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal / Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [targetFeatureName, setTargetFeatureName] = useState('');
  
  const initialFormState = { feature_name: '', color: '#3A7CEC', opacity: '1.00', layer_order: 0 };
  const [featureForm, setFeatureForm] = useState(initialFormState);
 
  const adminToken = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getFeatureConfig`, { headers });
      if (response.data.success) {
        setFeatures(response.data.data);
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch feature configurations.' });
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, [adminToken]);

  // --- Logic: Sorting ---
  const handleSortChange = (key) => {
    setSortConfig({ key, direction: 'asc' });
    setCurrentPage(1);
  };

  const sortedData = [...features].sort((a, b) => {
    const key = sortConfig.key;
    if (key === 'layer_order' || key === 'opacity') {
      return sortConfig.direction === 'asc' 
        ? parseFloat(a[key] || 0) - parseFloat(b[key] || 0)
        : parseFloat(b[key] || 0) - parseFloat(a[key] || 0);
    }
    
    const valA = String(a[key] || '').toLowerCase();
    const valB = String(b[key] || '').toLowerCase();
    
    if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // --- Logic: Filtering ---
  const filteredData = sortedData.filter(f => 
    f.feature_name && f.feature_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Logic: Pagination Math ---
  const totalRows = filteredData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  // --- Actions: Open Modals ---
  const openAddModal = () => {
    setModalMode('add');
    setTargetFeatureName('');
    setFeatureForm(initialFormState);
    setIsModalOpen(true);
  };

  const openEditModal = (feature) => {
    setModalMode('edit');
    setTargetFeatureName(feature.feature_name);
    setFeatureForm({
      feature_name: feature.feature_name,
      color: feature.color || '#000000',
      opacity: feature.opacity || '1.00',
      layer_order: parseInt(feature.layer_order, 10) || 0
    });
    setIsModalOpen(true);
  };

  // --- Actions: Form Submit ---
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (modalMode === 'add' && !featureForm.feature_name.trim()) {
      return Swal.fire({ icon: 'error', title: 'Validation Alert', text: 'Feature Name cannot be left completely blank.' });
    }

    try {
      let response;
      if (modalMode === 'add') {
        response = await axios.post(`${baseurl}addFeatureConfig`, featureForm, { headers });
      } else {
        response = await axios.post(`${baseurl}updateFeatureConfig`, {
          feature_name: targetFeatureName,
          color: featureForm.color,
          opacity: featureForm.opacity,
          layer_order: featureForm.layer_order
        }, { headers });
      }

      if (response.data.success) {
        Swal.fire({ icon: 'success', title: modalMode === 'add' ? 'Added!' : 'Updated!', text: 'Feature structure saved.', timer: 1500, showConfirmButton: false });
        setIsModalOpen(false);
        fetchFeatures();
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Submission Failed', text: err.response?.data?.message || 'Transaction error.' });
    }
  };

  // --- Actions: Delete Feature ---
  const handleDeleteFeature = (featureName) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Remove configuration rules for "${featureName}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.delete(`${baseurl}deleteFeatureConfig`, {
            headers,
            data: { feature_name: featureName }
          });
          if (response.data.success) {
            Swal.fire('Deleted!', 'Feature configuration removed.', 'success');
            fetchFeatures();
          }
        } catch (err) {
          Swal.fire('Error', err.response?.data?.message || 'Failed to delete configuration.', 'error');
        }
      }
    });
  };

  const SkeletonRow = () => (
    <tr className="skeleton-row">
      <td colSpan="5"><div className="skeleton-line"></div></td>
    </tr>
  );

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex', minHeight: '100vh' }}>
        <div className="profile-content-container p-4" style={{ flex: 1, overflow: 'auto' }}>
          
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="fw-bold mb-1" style={{ color: '#1B2559' }}>GIS Feature Layers</h2>
              <p className="text-muted m-0">Manage map configurations, ordering rules, and visibility parameters directly.</p>
            </div>
            <button 
              onClick={openAddModal} 
              className="btn btn-primary rounded-pill px-4 py-2 d-flex align-items-center gap-2 fw-semibold shadow-sm"
              style={{ background: '#4318FF', border: 'none' }}
            >
              <Plus size={18} /> Add New Layer
            </button>
          </div>

          {/* CONTROL BOX */}
          <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '15px' }}>
            <div className="row g-3 align-items-center">
              <div className="col-md-4">
                <div className="input-group border rounded-3 bg-light m-0">
                  <span className="input-group-text bg-transparent border-0">
                    <i className="fa-solid fa-magnifying-glass text-muted"></i>
                  </span>
                  <input 
                    type="text" 
                    className="form-control bg-transparent border-0 shadow-none" 
                    placeholder="Search feature name..." 
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <select 
                  className="form-select border-0 bg-light rounded-3" 
                  value={sortConfig.key}
                  onChange={(e) => handleSortChange(e.target.value)}
                >
                  <option value="layer_order">Sort by: Layer Order</option>
                  <option value="feature_name">Sort by: Feature Name</option>
                  <option value="opacity">Sort by: Opacity</option>
                </select>
              </div>
              {/* Row Limit Select to show more rows per page */}
              <div className="col-md-2 ms-auto text-end">
                <select 
                  className="form-select form-select-sm border bg-light rounded-3 d-inline-block w-auto"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setCurrentPage(1); }}
                >
                  <option value={10}>10 rows</option>
                  <option value={25}>25 rows</option>
                  <option value={50}>50 rows</option>
                  <option value={100}>100 rows</option>
                </select>
              </div>
            </div>
          </div>

          {/* TABLE CONTAINER */}
          <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4 border-0 text-muted small fw-bold">LAYER ORDER</th>
                    <th className="border-0 text-muted small fw-bold">FEATURE NAME</th>
                    <th className="border-0 text-muted small fw-bold text-center">COLOR SPEC</th>
                    <th className="border-0 text-muted small fw-bold text-center">OPACITY</th>
                    <th className="border-0 text-muted small fw-bold text-center">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [1,2,3].map(i => <SkeletonRow key={i} />)
                  ) : currentRows.length > 0 ? (
                    currentRows.map((feat) => (
                      <tr key={feat.feature_name}>
                        <td className="ps-4 text-dark fw-bold">
                          <span className="badge bg-light text-dark border p-2">
                            <Hash size={12} className="me-1 text-muted" /> {feat.layer_order}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-icon me-3"><Layers size={18} /></div>
                            <div>
                              <div className="fw-bold text-dark">{feat.feature_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="d-inline-flex align-items-center gap-2 bg-light px-3 py-1 rounded-pill border">
                            <span 
                              className="d-inline-block rounded-circle" 
                              style={{ 
                                width: '16px', 
                                height: '16px', 
                                backgroundColor: feat.color || '#000',
                                boxShadow: '0 0 4px rgba(0,0,0,0.2)' 
                              }}
                            />
                            <code className="text-uppercase small fw-bold text-secondary">{feat.color}</code>
                          </div>
                        </td>
                        <td className="text-center">
                          <span className="badge rounded-pill bg-soft-primary text-primary px-3">
                            <Sliders size={12} className="me-1" /> {Math.round(parseFloat(feat.opacity || 0) * 100)}%
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <button 
                              onClick={() => openEditModal(feat)}
                              className="btn btn-outline-primary btn-sm px-3 rounded-pill d-flex align-items-center gap-1"
                            >
                              <Edit3 size={14} /> Edit
                            </button>
                            <button 
                              onClick={() => handleDeleteFeature(feat.feature_name)}
                              className="btn btn-outline-danger btn-sm px-3 rounded-pill d-flex align-items-center gap-1"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-5 text-muted">No feature layers config found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* NEW VISUAL PAGINATION BAR COMPONENT */}
            <div className="card-footer bg-white border-0 p-3 d-flex justify-content-between align-items-center border-top">
              <span className="small text-muted fw-medium">
                Showing <b className="text-dark">{totalRows === 0 ? 0 : indexOfFirstRow + 1}</b> to{" "}
                <b className="text-dark">{Math.min(indexOfLastRow, totalRows)}</b> of{" "}
                <b className="text-dark">{totalRows}</b> entry items
              </span>
              <div className="d-flex align-items-center gap-2">
                <button 
                  className="btn btn-sm btn-light border rounded-pill p-2 d-flex align-items-center justify-content-center"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  style={{ width: '36px', height: '36px' }}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="small text-muted mx-2">
                  Page <b className="text-dark">{currentPage}</b> of {totalPages}
                </span>
                <button 
                  className="btn btn-sm btn-light border rounded-pill p-2 d-flex align-items-center justify-content-center"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  style={{ width: '36px', height: '36px' }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* UNIFIED MODAL OVERLAY */}
      {isModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-card card border-0 shadow-lg p-4">
            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
              <h5 className="fw-bold m-0" style={{ color: '#1B2559' }}>
                {modalMode === 'add' ? 'Create Feature Layer' : `Edit Config: ${targetFeatureName}`}
              </h5>
              <button className="btn p-1 border-0 text-muted" onClick={() => setIsModalOpen(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleFormSubmit}>
              <div className="mb-3">
                <label className="form-label small fw-bold text-muted">Feature Name Identifier</label>
                <input 
                  type="text"
                  className="form-control text-dark"
                  placeholder="e.g. water_pipelines"
                  value={featureForm.feature_name}
                  onChange={(e) => setFeatureForm({...featureForm, feature_name: e.target.value})}
                  disabled={modalMode === 'edit'}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label small fw-bold text-muted">Layer Color Code</label>
                <div className="d-flex gap-2 align-items-center">
                  <input 
                    type="color" 
                    className="form-control form-control-color border-0 p-0 bg-transparent" 
                    value={featureForm.color}
                    onChange={(e) => setFeatureForm({...featureForm, color: e.target.value})}
                    style={{ width: '45px', height: '40px' }}
                  />
                  <input 
                    type="text" 
                    className="form-control text-uppercase" 
                    value={featureForm.color} 
                    onChange={(e) => setFeatureForm({...featureForm, color: e.target.value})}
                    placeholder="#FFFFFF"
                    maxLength="7"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label small fw-bold text-muted">Opacity ({Math.round(featureForm.opacity * 100)}%)</label>
                <input 
                  type="range" 
                  className="form-range" 
                  min="0.00" 
                  max="1.00" 
                  step="0.05"
                  value={featureForm.opacity}
                  onChange={(e) => setFeatureForm({...featureForm, opacity: e.target.value})}
                />
              </div>

              <div className="mb-4">
                <label className="form-label small fw-bold text-muted">Layer Display Order Stack (Z-Index)</label>
                <input 
                  type="number" 
                  className="form-control" 
                  value={featureForm.layer_order}
                  onChange={(e) => setFeatureForm({...featureForm, layer_order: parseInt(e.target.value, 10) || 0})}
                  placeholder="e.g. 10"
                />
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <button type="button" className="btn btn-light rounded-pill px-4" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-1" style={{ background: '#4318FF', border: 'none' }}>
                  <Check size={16} /> {modalMode === 'add' ? 'Save New Layer' : 'Apply Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <style>{`
        .avatar-icon {
          width: 38px;
          height: 38px;
          background: #E9EDF7;
          color: #4318FF;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
        }
        .bg-soft-primary { background: #E9EDF7; }
        .skeleton-row td { padding: 25px 0; }
        .skeleton-line {
          height: 20px;
          width: 90%;
          margin: 0 auto;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite;
          border-radius: 4px;
        }
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .modal-backdrop {
          position: fixed;
          top: 0; left: 0; width: 100vw; height: 100vh;
          background: rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1050;
        }
        .modal-card {
          width: 100%;
          max-width: 420px;
          background: white;
          border-radius: 16px;
        }
      `}</style>
    </div>
  );
};

export default Featurelayers;